import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function HomeMeetKendrick() {
  const imgRef = useReveal(0);
  const textRef = useReveal(150);

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">

          {/* Photo */}
          <div ref={imgRef} className="reveal from-left relative pb-6 sm:pb-0">
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/c94b22b98d3b079186e68a7a22c21fa7.png"
                alt="Kendrick Taylor — Move With Kendrick Realtor"
                className="w-full h-[420px] sm:h-[520px] lg:h-[620px] object-cover object-top"
              />
            </div>
            {/* Floating badge — positioned inside the relative container so it never clips */}
            <div className="absolute bottom-0 right-0 sm:-bottom-5 sm:right-0 bg-[#1C3A6B] text-white px-5 py-3 sm:px-6 sm:py-4 rounded-lg">
              <p className="text-xs text-white/70 mb-1 uppercase tracking-wider">Based In</p>
              <p className="text-sm font-semibold">Temperance, Michigan</p>
            </div>
          </div>

          {/* Text */}
          <div ref={textRef} className="reveal from-right">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-5 font-medium">Meet Your Realtor</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl font-light text-[#111111] leading-tight mb-6">
              Meet <em className="not-italic font-semibold">Kendrick</em>
            </h2>
            <div className="w-12 h-0.5 bg-[#C8923A] mb-8"></div>
            <p className="text-[#555] leading-relaxed mb-5">
              Most agents can show you a house. I can tell you what's actually going on with the foundation, HVAC, roof, and electrical. Three years as a certified home inspector — before becoming a realtor — means you get a completely different level of insight.
            </p>
            <p className="text-[#555] leading-relaxed mb-8">
              I'm a former Sheriff's Deputy from Mecosta County who found his calling helping people understand homes. Honest assessments, no pressure, and a genuine care about whether you make a smart move — that's what I bring to every client.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                'Inspection-level knowledge of home systems',
                'Smooth transitions for buyers and sellers',
                'First-time buyer guidance that actually helps',
                'Estate, probate &amp; land sale expertise',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#444]">
                  <i className="ri-checkbox-circle-fill text-[#C8923A] text-lg mt-0.5 flex-shrink-0"></i>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C3A6B] hover:text-[#C8923A] transition-colors group whitespace-nowrap"
            >
              Read Kendrick's Full Story
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
